import { ReactNode, useMemo, useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Adg4Button } from "@/components/adg4/button/src";
import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { markXflowMlHeroActionTaken } from "@/components/user-context/helpers/cohorts";
import { useLocalizedHref } from "@/libraries/web-framework";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { ContactSalesLink } from "./reusable-xflow-hero-component/reusable-xflow-hero-templates";

const jiraWordBoldStyles = css({
    fontWeight: 700
});

/** Hides the line break on small viewports so the description flows without a forced break. */
const subtitleLineBreakDesktopOnlyStyles = css({
    [media.below.sm]: {
        display: "none"
    }
});

const TeamsFormatter = ({ children }: { children: ReactNode }) => (
    <Adg4TextUnderline
        underline={
            <svg
                width="100%"
                height="30"
                viewBox="0 0 306 42"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M0.314542 22.3672C52.9464 14.9125 91.8245 33.6666 144.966 18.1254C147.46 24.7349 153.892 33.0058 163.497 33.1094C155.486 32.9725 142.641 15.7178 155.536 27.2686C163.119 36.0683 196.172 25.2364 170.842 32.944C188.334 31.2885 204.356 20.5339 216.163 10.9702C235.811 -2.65832 220.928 23.6737 235.178 36.8756C255.37 55.2242 316.463 7.29572 304.134 7.28816C296.296 13.2947 285.769 17.046 281.423 23.6985C204.757 63.1757 259.992 -9.72674 214.311 2.55426C139.744 53.2533 177.393 7.4163 133.572 10.6223C108.06 28.9946 -0.613806 0.46356 0.29434 22.3672L0.314542 22.3672Z"
                    fill="#FFA900"
                />
            </svg>
        }
    >
        {children}
    </Adg4TextUnderline>
);

export const JiraLoggedInHeader = ({
    gwp10965XflowRecommendation = false,
    showJiraPlusRovoHomepageChanges = false
}: {
    gwp10965XflowRecommendation?: boolean;
    showJiraPlusRovoHomepageChanges?: boolean;
} = {}) => {
    const { accountId } = useLoggedInUser();
    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-software&edition=free"
    );
    const markMlHeroCtaAction =
        gwp10965XflowRecommendation && accountId
            ? () => markXflowMlHeroActionTaken(accountId)
            : undefined;

    const crossflowEventContainer = gwp10965XflowRecommendation
        ? "crossflowHeroSectionV2Variant"
        : "heroSection";

    const teamsFormatter = useCallback(
        (str: ReactNode) => <TeamsFormatter>{str}</TeamsFormatter>,
        []
    );

    const jiraWordBoldFormatter = useCallback(
        (str: ReactNode) => <span css={jiraWordBoldStyles}>{str}</span>,
        []
    );

    const formattedValues = useMemo(
        () => ({
            teams: teamsFormatter,
            br: <br />,
            jiraWord: jiraWordBoldFormatter,
            subtitleBr: <br css={subtitleLineBreakDesktopOnlyStyles} />
        }),
        [teamsFormatter, jiraWordBoldFormatter]
    );

    const renderHeroTitle = () => {
        if (showJiraPlusRovoHomepageChanges) {
            return (
                <FormattedMessage
                    id="HomePageTemplateV2.jiraHeroGwp10906"
                    defaultMessage="Unleash your{br}<teams>human-AI</teams> super team"
                    values={formattedValues}
                />
            );
        }
        if (gwp10965XflowRecommendation) {
            return (
                <FormattedMessage
                    id="HomePageTemplateV2.jiraHeroVariant"
                    defaultMessage="<jiraWord>Jira</jiraWord> projects that practically{br}<teams>manage</teams> themselves"
                    values={formattedValues}
                />
            );
        }
        return (
            <FormattedMessage
                id="HomePageTemplateV2.jiraHeroGwp9758"
                defaultMessage="Projects that practically{br}<teams>manage</teams> themselves"
                values={formattedValues}
            />
        );
    };

    const renderSubtitle = () => {
        if (showJiraPlusRovoHomepageChanges) {
            return (
                <p css={descriptionStyles} className="hero-description">
                    <FormattedMessage
                        id="HomePageTemplateV2.jiraHeroGwp10906Description"
                        defaultMessage="Everyone. Working on the right things, including your AI agents.{br}Jira is where teams and agents come together to plan, execute, and deliver outcomes."
                        values={formattedValues}
                    />
                </p>
            );
        }
        return (
            <p css={descriptionStyles} className="hero-description">
                <FormattedMessage
                    id="HomePageTemplateV2.jiraHeroGwp9758Description"
                    defaultMessage="Introducing project management skills in Jira.{subtitleBr} Dream bigger and move faster with AI."
                    values={formattedValues}
                />
            </p>
        );
    };

    return (
        <div css={containerStyles}>
            <h1 className="superhero-contrast" css={heroTitleStyles}>
                {renderHeroTitle()}
            </h1>
            {renderSubtitle()}
            <div css={ctaContainerStyles}>
                <Adg4Button
                    variant="primary"
                    color="blue"
                    href={signupLink}
                    onClick={markMlHeroCtaAction}
                    dataEventProps={{
                        "data-event": "clicked",
                        "data-event-component": "linkButton",
                        "data-event-container": crossflowEventContainer,
                        "data-product-key": "home",
                        "data-label-english": "Get started"
                    }}
                    css={heroGetStartedStyles}
                >
                    <FormattedMessage
                        id="HomePageTemplateV2.jiraHero.getStartedCtaButton"
                        defaultMessage="Get started"
                    />
                </Adg4Button>
                {gwp10965XflowRecommendation && (
                    <ContactSalesLink onAction={markMlHeroCtaAction} />
                )}
            </div>
        </div>
    );
};

const containerStyles = css({
    display: "grid",
    justifyItems: "center"
});

const heroTitleStyles = css({
    marginBlock: "19px",
    textAlign: "center"
});

const ctaContainerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "24px",
    marginBlockStart: "24px",
    marginBlockEnd: "40px",
    [media.below.sm]: {
        marginBlockStart: "20px",
        marginBlockEnd: "45px"
    }
});

const heroGetStartedStyles = css({
    marginBlock: 0
});

const descriptionStyles = css({
    ["&.hero-description"]: {
        fontSize: "1.25rem",
        lineHeight: "1.4",
        textAlign: "center",
        margin: "15px auto 10px"
    }
});
